/**
 * Warm the next chapter's stills/posters so a wipe doesn't wait on image
 * fetches. The active chapter is already in the DOM (`loading="eager"`), so
 * preloading it again only duplicated the request. Priority stays low so
 * this doesn't compete with the loops on screen.
 */

import { useEffect } from "react";

import { chapterWarmUrls } from "@story/data-centers-hub/config/chapterWarmUrls";
import type { DeckSlideId } from "@story/data-centers-hub/config/slides";

function warmImage(href: string): HTMLLinkElement {
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = href;
  // Hint only. A second `Image()` decoded the same bytes again and was
  // dropped as soon as this function returned, so it never pinned a bitmap.
  link.setAttribute("fetchpriority", "low");
  document.head.appendChild(link);
  return link;
}

export function useHubMediaWarmup(
  activeSlideId: DeckSlideId,
  deck: ReadonlyArray<{ id: DeckSlideId }>,
): void {
  useEffect(() => {
    const index = deck.findIndex((slide) => slide.id === activeSlideId);
    const nextId = index >= 0 ? deck[index + 1]?.id : undefined;
    if (!nextId) {
      return;
    }

    const seen = new Set<string>();
    const links: HTMLLinkElement[] = [];
    for (const href of chapterWarmUrls(nextId)) {
      if (seen.has(href)) {
        continue;
      }
      seen.add(href);
      links.push(warmImage(href));
    }

    return () => {
      for (const link of links) {
        link.remove();
      }
    };
  }, [activeSlideId, deck]);
}
